import {
  NON_PUBLISHED_POST_STATES,
  PUBLISHED_POST_STATES,
} from '~/constants/content'
import buildVideosQuery from '@/queries/videos'
import buildArticlesBaseQuery from '@/queries/articles-base'
import buildArticlesSimpleQuery from '@/queries/articles-simple'
import buildVideosByCategoriesQuery from '@/queries/videos-by-categories'
import buildArticlesByCategoriesQuery from '@/queries/articles-by-categories'
import buildArticlesByTagsQuery from '@/queries/articles-by-tags'
import buildArticleWithSeo from '@/queries/articles-with-seo'
import buildVideoWithSeo from '@/queries/video-with-seo'
import buildMostRecentFeaturedVideosQuery from '@/queries/featured-videos'
import buildMostRecentFeaturedVideoByCategoryQuery from '@/queries/featured-video-by-category'
import buildFeaturedArticlesQuery from '@/queries/featured-articles'
import buildFeaturedArticlesByCategoriesQuery from '@/queries/featured-articles-by-categories'
import buildFeaturedArticlesByTagsQuery from '@/queries/featured-articles-by-tags'
import getFeaturedSubCategory from '@/queries/featured-subcategory.gql'
import getAuthenticatedUserDataQuery from '@/queries/authenticated-user.gql'
import featuredSection from '@/queries/featured-section.gql'
import buildContentByCategoriesQuery from '@/queries/contents-by-categories'
import buildContentsByTagsQuery from '@/queries/contents-by-tags'
import buildVideoDetailByIdQuery from '@/queries/video-detail-by-id'
import buildVideoDetailBySlugQuery from '@/queries/video-detail-by-slug'
import buildArticleDetailBySlugQuery from '@/queries/article-detail-by-slug'
import buildArticleDetailByIdQuery from '@/queries/article-detail-by-id'
import type { Article, Video } from '@/models/content'
import {
  getAdaptedArticle,
  getAdaptedArticles,
  getAdaptedContents,
  getAdaptedFeaturedCategoryData,
  getAdaptedFeaturedSubcategory,
  getAdaptedVideo,
  getAdaptedVideos,
} from '@/utils/adapters'
import allCategories from '@/constants/categories'
import buildArticlesAndVideosQuery from '@/queries/articles-and-videos'
import type { NuxtApp } from '#app'
import { createError } from '#app'
import { logError } from '~/utils/log-error'

const MAX_ITEMS_PER_QUERY = 12

export default defineNuxtPlugin(({ $apollo, $config }: NuxtApp) => {
  const apolloClient = $config.public.isPreviewSite
    ? $apollo.privateClient
    : $apollo.publicClient

  const getStatuses = () => {
    const postStates = [...PUBLISHED_POST_STATES]

    if ($config.public.isPreviewSite) {
      postStates.push(...NON_PUBLISHED_POST_STATES)
    }

    return postStates
  }

  return {
    provide: {
      graphqlClient: {
        async getVideos({
          after = null,
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          search = '',
        } = {}) {
          try {
            const { data } = await apolloClient.query({
              query: buildVideosQuery({ statuses: getStatuses() }),
              variables: {
                first: itemsPerQuery,
                after,
                search,
              },
            })

            const { hasNextPage, endCursor } = data.videos.pageInfo
            const videos = getAdaptedVideos(data)

            return {
              data: videos,
              hasNextPage,
              endCursor,
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading videos',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'videos',
                  params: { after, itemsPerQuery, search },
                },
              },
            })
          }
        },

        async getVideosByCategories({
          after = null,
          categories = [],
          events = [],
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          search = '',
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildVideosByCategoriesQuery({ statuses: getStatuses() }),
              variables: {
                first: itemsPerQuery,
                after,
                categories: categories.length ? categories : allCategories,
                events,
                search,
              },
            })

            const { hasNextPage, endCursor, total } = data.videos.pageInfo
            const videos = getAdaptedVideos(data)

            return {
              data: videos,
              hasNextPage,
              endCursor,
              total,
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading videos by categories',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'videosByCategories',
                  params: { categories, events, search },
                },
              },
            })
          }
        },

        async getFeaturedVideos({
          after = null,
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
        } = {}) {
          try {
            const { data } = await apolloClient.query({
              query: buildMostRecentFeaturedVideosQuery({
                statuses: getStatuses(),
              }),
              variables: {
                first: itemsPerQuery,
                after,
              },
            })
            const videos = getAdaptedVideos(data)
            return videos.length ? videos : []
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured videos',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'featuredVideos',
                  params: { after, itemsPerQuery },
                },
              },
            })
          }
        },

        async getFeaturedVideo(): Promise<Video> | null {
          try {
            const videos = await this.getFeaturedVideos({
              itemsPerQuery: 1,
            })
            return videos.length ? videos[0] : null
          } catch (error) {
            // Error already handled by getFeaturedVideos, but we can add additional handling here if needed
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured video',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: { type: 'featuredVideo' },
              },
            })
          }
        },

        async getArticlesByTags({
          tags = [],
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          excluded = [],
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildArticlesByTagsQuery({
                operator: 'AND',
                statuses: getStatuses(),
              }),
              variables: { tags, first: itemsPerQuery, excluded },
            })

            return getAdaptedArticles(data)
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading articles by tags',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'articlesByTags',
                  params: { tags, itemsPerQuery, excluded },
                },
              },
            })
          }
        },

        async getFeaturedArticlesByTags({
          tags = [],
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildFeaturedArticlesByTagsQuery({
                statuses: getStatuses(),
              }),
              variables: { tags, first: itemsPerQuery },
            })

            return getAdaptedArticles(data)
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured articles by tags',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'featuredArticlesByTags',
                  params: { tags, itemsPerQuery },
                },
              },
            })
          }
        },

        async getFeaturedVideoByCategory({ category }: { category: string }) {
          try {
            const { data } = await apolloClient.query({
              query: buildMostRecentFeaturedVideoByCategoryQuery({
                statuses: getStatuses(),
              }),
              variables: {
                category,
              },
            })
            const video = getAdaptedVideos(data)
            return video.length ? video[0] : null
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured video by category',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'featuredVideoByCategory',
                  params: { category },
                },
              },
            })
          }
        },

        async getVideoDetail({ slug }: { slug: string }) {
          try {
            const hasSlug = /[a-zA-Z]/.exec(slug)
            const isDraft = !hasSlug

            if (isDraft && !$config.public.isPreviewSite) {
              return null
            }

            const response = hasSlug
              ? await $apollo.getClient().query({
                  query: buildVideoDetailBySlugQuery(),
                  variables: {
                    slug,
                  },
                })
              : await $apollo.getClient().query({
                  query: buildVideoDetailByIdQuery(),
                  variables: {
                    id: slug,
                  },
                })

            // Check for errors in the response
            if (response.errors && response.errors.length > 0) {
              throw new Error(
                response.errors[0].message || 'Error fetching video',
              )
            }

            const { data } = response

            if (!data.video) return null

            const video = getAdaptedVideo(data)
            const categories = data.video.categories.nodes.map(
              (category: { name: string }) => category.name,
            )

            const relatedResponse = await apolloClient.query({
              query: buildVideosByCategoriesQuery({ statuses: getStatuses() }),
              variables: {
                first: MAX_ITEMS_PER_QUERY,
                categories,
                events: [],
              },
            })

            if (relatedResponse.errors && relatedResponse.errors.length > 0) {
              logError(relatedResponse.errors[0]?.message)
              video.relatedVideos = []
            } else {
              video.relatedVideos = getAdaptedVideos(relatedResponse.data)
            }

            return video
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading video',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: { type: 'video', slug },
              },
            })
          }
        },

        async getArticlesAndVideos({
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          excluded = [],
        } = {}) {
          try {
            const { data } = await apolloClient.query({
              query: buildArticlesAndVideosQuery({ statuses: getStatuses() }),
              variables: {
                first: itemsPerQuery,
                excluded,
              },
            })

            const videos = getAdaptedVideos(data)
            const articles = getAdaptedArticles(data)
            const sorted = [...articles, ...videos]
              .sort(
                (first, second) =>
                  new Date(second.date).getTime() -
                  new Date(first.date).getTime(),
              )
              .slice(0, itemsPerQuery)

            return {
              data: sorted,
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading content',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'articlesAndVideos',
                  params: { itemsPerQuery, excluded },
                },
              },
            })
          }
        },

        async getArticles({
          after = null,
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          includeFeatured = false,
          excluded = [],
          detailed = true,
        } = {}) {
          try {
            const query = detailed
              ? buildArticlesBaseQuery({ statuses: getStatuses() })
              : buildArticlesSimpleQuery({
                  statuses: getStatuses(),
                })
            const { data } = await apolloClient.query({
              query,
              variables: {
                first: itemsPerQuery,
                after,
                includeFeatured,
                excluded,
              },
            })

            const { hasNextPage, endCursor } = data.articles.pageInfo
            const articles = getAdaptedArticles(data)

            return {
              data: articles,
              hasNextPage,
              endCursor,
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading articles',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'articles',
                  params: {
                    after,
                    itemsPerQuery,
                    includeFeatured,
                    excluded,
                    detailed,
                  },
                },
              },
            })
          }
        },

        async getArticleDetail({ slug }: { slug: string | number }) {
          try {
            const hasSlug = /[a-zA-Z]/.exec(String(slug))
            const isDraft = !hasSlug

            if (isDraft && !$config.public.isPreviewSite) {
              return null
            }

            const response = hasSlug
              ? await $apollo.getClient().query({
                  query: buildArticleDetailBySlugQuery(),
                  variables: { slug },
                })
              : await $apollo.getClient().query({
                  query: buildArticleDetailByIdQuery(),
                  variables: { id: slug },
                })

            if (response.errors && response.errors.length > 0) {
              throw new Error(
                response.errors[0].message || 'Error fetching article',
              )
            }

            const { data } = response

            if (!data.article) return null

            const article = getAdaptedArticle(data)
            const categories = data.article.categories.nodes.map(
              (category: { name: string }) => category.name,
            )

            const { data: relatedData } = await apolloClient.query({
              query: buildArticlesByCategoriesQuery({
                operator: 'IN',
                statuses: getStatuses(),
              }),
              variables: {
                first: MAX_ITEMS_PER_QUERY,
                categories,
                excluded: [article.id],
              },
            })

            article.relatedArticles = getAdaptedArticles(relatedData)

            return article
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading article',
              fatal: true,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: { type: 'article', slug },
              },
            })
          }
        },

        async getArticlesByCategories({
          after = null,
          categories = [],
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          excluded = [],
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildArticlesByCategoriesQuery({
                operator: 'AND',
                statuses: getStatuses(),
              }),
              variables: {
                first: itemsPerQuery,
                after,
                categories,
                excluded,
              },
            })

            const { hasNextPage, endCursor } = data.articles.pageInfo
            const articles = getAdaptedArticles(data)

            return {
              data: articles,
              hasNextPage,
              endCursor,
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading articles by categories',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'articlesByCategories',
                  params: { after, categories, itemsPerQuery, excluded },
                },
              },
            })
          }
        },

        async getFeaturedArticles({
          itemsPerQuery,
        }: {
          itemsPerQuery: number
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildFeaturedArticlesQuery({ statuses: getStatuses() }),
              variables: {
                first: itemsPerQuery,
              },
            })

            const articles = getAdaptedArticles(data)

            const missingArticles = itemsPerQuery - articles.length

            if (missingArticles) {
              try {
                const { data: offsetArticles } = await this.getArticles({
                  itemsPerQuery: missingArticles,
                  excluded: articles.map((article) => article.id) as never[],
                })
                articles.push(...offsetArticles)
              } catch (offsetError) {
                logError(offsetError)
              }
            }

            return articles
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured articles',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'featuredArticles',
                  params: { itemsPerQuery },
                },
              },
            })
          }
        },

        async getFeaturedArticlesByCategories({
          itemsPerQuery = 10,
          categories = [],
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildFeaturedArticlesByCategoriesQuery({
                statuses: getStatuses(),
              }),
              variables: {
                first: itemsPerQuery,
                categories,
              },
            })

            const articles = getAdaptedArticles(data)

            const missingArticles = itemsPerQuery - articles.length

            if (missingArticles) {
              try {
                const { data: offsetArticles } =
                  await this.getArticlesByCategories({
                    itemsPerQuery: missingArticles,
                    categories,
                    excluded: [
                      ...articles.map((article) => article.id),
                    ] as never[],
                  })
                articles.push(...offsetArticles)
              } catch (offsetError) {
                logError(offsetError)
              }
            }

            return articles
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured articles by categories',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'featuredArticlesByCategories',
                  params: { itemsPerQuery, categories },
                },
              },
            })
          }
        },

        async getFeaturedSubcategoryArticles({
          category,
          itemsPerQuery,
          excluded = [],
        }: {
          category: string
          itemsPerQuery: number
          excluded?: never[]
        }) {
          try {
            const { data } = await apolloClient.query({
              query: getFeaturedSubCategory,
              variables: {
                first: itemsPerQuery,
                category,
              },
            })

            const featuredSubcategory = getAdaptedFeaturedSubcategory(data)

            const articles: Article[] = []

            if (featuredSubcategory) {
              try {
                const { data } = await this.getArticlesByCategories({
                  itemsPerQuery,
                  categories: [category, featuredSubcategory] as never[],
                  excluded,
                })

                articles.push(...data)
              } catch (articlesError) {
                logError(articlesError)
              }
            }

            return articles
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured subcategory articles',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'featuredSubcategoryArticles',
                  params: { category, itemsPerQuery },
                },
              },
            })
          }
        },

        async getFeaturedSection(category: string) {
          try {
            const response = await apolloClient.query({
              query: featuredSection,
              variables: {
                category,
              },
            })
            return getAdaptedFeaturedCategoryData(response)
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading featured a section',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: { type: 'featuredSection', params: { category } },
              },
            })
          }
        },

        async getContentsByCategories({
          categories = [],
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          excluded = [],
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildContentByCategoriesQuery({ statuses: getStatuses() }),
              variables: {
                first: itemsPerQuery,
                categories,
                excluded,
              },
            })

            return {
              total: data.contentNodes.pageInfo.total,
              data: getAdaptedContents(data),
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading contents by categories',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'contentsByCategories',
                  params: { categories, itemsPerQuery, excluded },
                },
              },
            })
          }
        },

        async getContentsByTags({
          tags = [],
          itemsPerQuery = MAX_ITEMS_PER_QUERY,
          excluded = [],
        }) {
          try {
            const { data } = await apolloClient.query({
              query: buildContentsByTagsQuery({ statuses: getStatuses() }),
              variables: {
                first: itemsPerQuery,
                tags,
                excluded,
              },
            })

            return {
              total: data.contentNodes.pageInfo.total,
              data: getAdaptedContents(data),
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading contents by tags',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'contentsByTags',
                  params: { tags, itemsPerQuery, excluded },
                },
              },
            })
          }
        },

        async getSeoInformation(slug: string) {
          try {
            const { data } = await apolloClient.query({
              query: buildArticleWithSeo({ slug }),
            })

            return data
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading SEO information',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: { type: 'seoInformation', params: { slug } },
              },
            })
          }
        },

        async getVideoSeoInformation(slug: string) {
          try {
            const { data } = await apolloClient.query({
              query: buildVideoWithSeo({ slug }),
            })

            return data
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error loading video SEO information',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: { type: 'videoSeoInformation', params: { slug } },
              },
            })
          }
        },

        async getAuthenticatedUserData() {
          try {
            const { data } = await $apollo.privateClient.query({
              query: getAuthenticatedUserDataQuery,
            })

            return {
              email: data.viewer?.email,
              company: data.viewer?.userData?.company,
            }
          } catch (error) {
            logError(error)
            throw createError({
              statusCode: 500,
              statusMessage: 'Error getting user data',
              fatal: false,
              data: {
                originalError: error,
                retryable: true,
                requestInfo: {
                  type: 'getAuthenticatedUserData',
                  params: undefined,
                },
              },
            })
          }
        },
      },
    },
  }
})
